// components/artifacts/charts/RadarChartComponent.tsx
import React from "react";
import { 
  RadarChart, 
  Radar, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface RadarChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  colors: string[];
  id?: string;
}

// Custom tooltip formatter that includes units
const formatTooltipValue = (value: any, unit?: string) => {
  if (!unit) return value;
  
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${value}`;
    case '%':
      return `${value}${unit}`;
    default:
      return `${value} ${unit}`;
  }
};

export const RadarChartComponent: React.FC<RadarChartComponentProps> = React.memo(({
  data,
  config,
  colors,
  id,
}) => {
  const categoryKey = data.categoryKey || "category";
  const firstItem = data.data[0] || {};
  const radarKeys = Object.keys(firstItem).filter((key) => key !== categoryKey);
  const { valueUnit } = data;

  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {valueUnit && (
        <div className="mb-2 text-sm text-muted-foreground">
          Values: {valueUnit}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[400px] w-full"
      >
        <RadarChart accessibilityLayer cx="50%" cy="50%" outerRadius="80%" data={data.data}>
          <PolarGrid />
          <PolarAngleAxis dataKey={categoryKey} />
          <PolarRadiusAxis 
            angle={30} 
            domain={[0, "auto"]} 
            tickFormatter={(value) => {
                let y = value
                //if value is number, convert to string
                if (typeof value === 'number') {
                  y = formatNumber(y);
                } 
              
                if (!valueUnit) return value;
                
                // Handle different valueUnit types
                switch (valueUnit) {
                  case '$':
                  case '€':
                  case '£':
                  case '¥':
                    return `${valueUnit}${y}`;
                  case '%':
                    return `${y}${valueUnit}`;
                  default:
                    return `${y} ${valueUnit}`;
                }
            }}
          />
          {radarKeys.map((key, index) => (
            <Radar
              key={key}
              name={key}
              dataKey={key}
              stroke={colors[index]}
              fill={colors[index]}
              fillOpacity={0.2}
            />
          ))}
          <ChartLegend content={<ChartLegendContent />} />
          <ChartTooltip 
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, valueUnit)}
            />} 
          />
        </RadarChart>
      </ChartContainer>
    </div>
  );
});